import { useEffect, useState, useRef, useContext, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../PrescriptionList.css";
import Localize from "../Localize";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuContent,
} from "../ui/dropdown-menu";

import { toast } from "sonner";
import {
  Clock,
  Eye,
  FileText,
  Info,
  LayoutDashboard,
  LogIn,
  LogOut,
  Play,
  Save,
  SquarePen,
} from "lucide-react";

import { useEffectOnce } from "../../hooks/useEffectOnce";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import { distributionService } from "../../services/distributionService";
import LoggerContext from "../../context/LoggerProvider";
import PresListContext from "../../context/PresListProvider";
import { WSContext } from "../../App";
import { LocationState, LocationObject } from "../../lib/types";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "../ui/drawer";
import ChatPanel from "./chatPanel";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import ActivityRunningPreview from "./activityRunningPreview";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
// import moment from "moment";
import SaveAndContinueModal from "./modals/saveAndContinueModal";
import ActivityCompletionModal from "./modals/activityCompletionModal";
import { redirectOnLogout } from "../../lib/logout";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";

const mockExamples = [
  {
    id: "example-1",
    title: "First Activity",
    description:
      "A complete walkthrough of deciding whether to launch a new product feature",
    lastUpdated: "2 days ago",
    isOutdated: false,
    currentStep: 3,
    totalSteps: 5,
  },
  {
    id: "example-2",
    title: "Second Activity",
    description: "Example of using this framework for organizational changes",
    lastUpdated: "1 week ago",
    isOutdated: true,
    currentStep: 2,
    totalSteps: 4,
  },
  {
    id: "example-3",
    title: "Third Activity",
    description: "How to apply decision criteria to financial planning",
    lastUpdated: "3 days ago",
    isOutdated: false,
    currentStep: 1,
    totalSteps: 5,
  },
];

const mockAuthor = {
  name: "Umer Pall",
  profilePicture: "/placeholder.svg?height=40&width=40",
  website: "https://sarahchen.com",
  bio: "Clear your thinking with this activity. Run it.",
};

function displayDateFromTimestamp(ts: string) {
  // moment(activityLastUpdatedDate).fromNow() always shows in English, need to localize if we will use it
  const d = new Date(ts);
  const year = d.getFullYear();
  const yearText = String(year).padStart(4, "0");
  const month = d.getMonth();
  const monthText = String(month).padStart(2, "0");
  const dayOfMonth = d.getDate();
  const dayOfMonthText = String(dayOfMonth).padStart(2, "0");
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(dayOfMonth)
  ) {
    return null;
  }
  return `${yearText}-${monthText}-${dayOfMonthText}`;
}

const WorkflowInfoDialog = ({
  showWorkflowInfoDialog,
  setShowWorkflowInfoDialog,
  activityLastUpdatedDate,
  prescriptionVersionNo,
  userInfo,
}: any) => {
  const { t } = useTranslation();
  return (
    <Dialog
      open={showWorkflowInfoDialog}
      onOpenChange={setShowWorkflowInfoDialog}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("title_about_this_activity")}</DialogTitle>
          {/* <DialogDescription>
            Information about the activity author and details
          </DialogDescription> */}
        </DialogHeader>

        <div className="space-y-6">
          {/* Author Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t("label_created_by")}</h3>
            <div className="flex items-start gap-3">
              {/* <Avatar className="h-12 w-12">
                <AvatarImage
                  src={mockAuthor.profilePicture || "/placeholder.svg"}
                />
                <AvatarFallback>SC</AvatarFallback>
              </Avatar> */}
              <div className="h-8 w-8 rounded-full overflow-hidden">
                <img
                  src={`https://ui-avatars.com/api/?name=${
                    userInfo?.email.split("@")[0]
                  }`}
                  alt="Avatar"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium">{userInfo?.email || "N/A"}</h4>
                  {/* <Button variant="ghost" size="sm" className="h-6 px-2">
                    <ExternalLink className="h-3 w-3" />
                  </Button> */}
                </div>
                {/* <p className="text-sm text-muted-foreground">
                  {mockAuthor.bio}
                </p> */}
              </div>
            </div>
          </div>

          {/* Workflow Details Section - Placeholder for future expansion */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t("activity_details")}</h3>
            <div className="space-y-2 text-sm">
              {/*
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("label_version")}</span>
                <span>{prescriptionVersionNo || "N/A"}</span>
              </div>
              */}
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("label_published")}
                </span>
                <span>
                  {displayDateFromTimestamp(activityLastUpdatedDate) ??
                    t("unknown")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ExamplesDialog = ({
  showExamplesDialog,
  setShowExamplesDialog,
  lastViewedExample,
  setLastViewedExample,
}: any) => (
  <Dialog open={showExamplesDialog} onOpenChange={setShowExamplesDialog}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Example Engagements</DialogTitle>
        <DialogDescription>
          Review prepared examples to see how others have used this workflow
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {lastViewedExample && (
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Continue Previous Example</h4>
              <Badge variant="secondary">
                <Clock className="h-3 w-3 mr-1" />
                Recently Viewed
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">
                {mockExamples.find((ex) => ex.id === lastViewedExample)?.title}
              </span>
              <Button
                size="sm"
                onClick={() => {
                  // Fast-forward to current screen in the example
                  setShowExamplesDialog(false);
                  // Implementation would load the example and navigate to current step
                }}
              >
                <Play className="h-4 w-4 mr-1" />
                Continue
              </Button>
            </div>
          </div>
        )}

        <div className="grid gap-3">
          {mockExamples.map((example) => (
            <div
              key={example.id}
              className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium">{example.title}</h3>
                    {example.isOutdated && (
                      <Badge variant="destructive" className="text-xs">
                        Outdated
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {example.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Updated {example.lastUpdated}</span>
                    <span>
                      {example.currentStep}/{example.totalSteps} steps
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLastViewedExample(example.id);
                      setShowExamplesDialog(false);
                      // Implementation would load example in review mode
                      // If current screen doesn't exist, start from beginning
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Review
                  </Button>
                </div>
              </div>

              {example.isOutdated && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  This example may be outdated due to workflow changes. Author
                  should review and update.
                </div>
              )}
            </div>
          ))}
        </div>

        {mockExamples.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No examples available for this workflow</p>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

const RunActivityPage = () => {
  const { auth, setAuth } = useAuth();
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();

  // const [showExamplesDialog, setShowExamplesDialog] = useState(false);
  const [showWorkflowInfoDialog, setShowWorkflowInfoDialog] = useState(false);
  // const [lastViewedExample, setLastViewedExample] = useState("");
  const [activityLastUpdatedDate, setActivityLastUpdatedDate] = useState("");
  const [prescriptionVersionNo, setPrescriptionVersionNo] = useState("");
  const [saveAndExitDestination, setSaveAndExitDestination] = useState("");
  const [isSaveAndExitModalOpen, setIsSaveAndExitModalOpen] = useState(false);
  const [isActivityCompletedModalOpen, setIsActivityCompletedModalOpen] =
    useState(false);
  const [processId, setProcessId] = useState("");
  const [prescriptionId, setPrescriptionId] = useState("");

  const saveChanges = useRef(true);

  // const [searchParams, setSearchParams] = useSearchParams();

  //   const [reference, setReference] = useState(null);

  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 800px)").matches
  );

  const { Logger } = useContext(LoggerContext);

  const {
    // refreshLists,
    prescriptions,
    // publicPrescriptions,
    // processes,
    // expertProcesses,
    setPrescriptions,
    setPublicPrescriptions,
    setProcesses,
    setExpertProcesses,
  } = useContext(PresListContext);

  const {
    socket,
    roomId,
    setRoomId,
    globalAbortController,
    listRefresh,
    setListRefresh,
  } = useContext(WSContext);

  const location = useLocation();
  const navigate = useNavigate();

  /**
   * The `setSearchParams` function from `useSearchParams` causes a navigation,
   * so we use the `replaceState` function from the browser's History API instead.
   * @param patch object with key-value pairs to set or update in the query string
   */
  function updateSearchParams(patch: object) {
    /*
    setSearchParams((current) => {
      Object.entries(patch).forEach(([key, value]) => {
        current.set(key, value);
      });
      return current;
    });
    */
    const browserSearchParams = new URLSearchParams(window.location.search);
    Object.entries(patch).forEach(([key, value]) => {
      browserSearchParams.set(key, value);
    });
    window.history.replaceState({}, "", "?" + browserSearchParams.toString());
  }

  function getLocationState() {
    if (location.state && typeof location.state === "object") {
      return location.state as LocationState;
    }
    return null;
  }

  const state = getLocationState();

  const defaultFromLocation = { pathname: "/portal" };

  // the "from" location is where the user came from,
  // which we should also use as the destination when the
  // user exits the activity without completing it
  let from: LocationObject = state?.from ?? defaultFromLocation;

  // the "forward" location is set by the caller and specifies
  // where to send the user when they complete the activity.
  // if the user cancels or exits early without completing,
  // they would be redirected back to the "from" location
  let forward: LocationObject = state?.forward ?? from;


  // background shows that PREVIEW should be opened as modal on top of current page!
  let background: any | null = state?.background ?? { pathname: "/portal" };
  if (background?.pathname === "/") {
    background = null;
  }
  /*
  if (location.state && typeof location.state === "object") {
    const state = location.state as LocationState;
    if (state.background && typeof state.background === "object") {
      if ("pathname" in state.background) {
        background =
          state.background.pathname !== "/" ? state.background : null;
      }
    }
  }
  */

  const selectedPrescription = useMemo(() => {
    if (!prescriptions?.length) return;
    const findInQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      return searchParams.get("wid"); // null if not defined
    };
    const findInStorage = () => {
      return sessionStorage.getItem("prescriptionID");
    };
    const selectedPrescriptionId = findInQuery() ?? findInStorage();
    if (!selectedPrescriptionId) {
      return { name: "N/A" };
    }
    const foundPrescription = prescriptions?.find(
      (prescription: any) => prescription._id === selectedPrescriptionId
    );
    setPrescriptionId(foundPrescription?._id ?? "");
    console.log(`updating search params 2`);
    updateSearchParams({ wid: foundPrescription?._id ?? "" });
    console.log(`selectedPrescription set wid ${foundPrescription?._id ?? ""}`);
    return foundPrescription || { name: "N/A" };
  }, []);

  const userInfo = useMemo(() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : null;
  }, []);

  const isAuthenticated = useMemo(() => {
    if (userInfo?.email) {
      return true;
    }
    return false;
  }, [userInfo]);

  async function loadPrescriptionMetadata(id: string) {
    if (id) {
      console.log(`fetching activity metadata for id: ${id}`);
      const activityMetadataResponse = await retryFetch(
        `${config.API_BASE}/api/prescriptions/${id}/metadata`,
        {
          credentials: "include",
        }
      );
      if (activityMetadataResponse.ok) {
        console.log(`got activity metadata for id: ${id}`);
        const activityMetadata = await activityMetadataResponse.json();
        console.log(
          `got activity metadata for id [${id}]: ${JSON.stringify(
            activityMetadata
          )}`
        );
        setActivityLastUpdatedDate(activityMetadata.lastUpdated);
      }
    }
  }

  useEffectOnce(() => {
    const handler = (e: any) => setIsMobile(e.matches);
    window.matchMedia("(max-width: 800px)").addEventListener("change", handler);

    sessionStorage.removeItem("prescriptionID");
    sessionStorage.removeItem("prescriptionHistoryID");
    sessionStorage.removeItem("runProcessID");

    // refreshLists();

    // subscribe to incoming messages
    let messageUnsubscribe = distributionService
      .getMessage()
      .subscribe((msg) => {
        Logger.debug("PrescriptionList incoming message: ");
        Logger.debug(msg);

        switch (msg.command) {
        }
      });

    return () => {
      messageUnsubscribe.unsubscribe();
    };
  }, []);

  useEffect(() => {
    loadPrescriptionMetadata(prescriptionId);
  }, [prescriptionId]);

  useEffect(() => {
    if (listRefresh) {
      // refreshLists(); // commented out because we don't need an updated activities list when running an already-selected activity
      setListRefresh(false);
    }
  }, [listRefresh]);

  const logout = async () => {
    // Close room first
    if (roomId) {
      Logger.info("Message LEAVEROOM has been sent!");
      socket.send(
        JSON.stringify({
          command: "leaveroom",
          payload: undefined,
        })
      );
      socket.send(
        JSON.stringify({
          command: "changeuserstate",
          payload: "online",
        })
      );
      Logger.debug("HEADER: ROOM ID SET TO 0");
      setRoomId(0);
    }

    // abort with globalAbortController (mainly for loading prescription lists)
    sessionStorage.setItem("semaphore", "false");
    globalAbortController.abort();

    let response = await fetch(config.API_BASE + "/api/auth/logout", {
      credentials: "include",
    });
    Logger.debug(`logout response status ${response.status}`);

    if (!response.ok) alert(t("msg_error_logout"));

    if (response.status == 200) {
      try {
        let result = await response.json();
        Logger.debug(result);
      } catch (err) {
        Logger.debug(`failed to parse logout response`, err);
      }
    }
    setAuth({ email: "", accessToken: "", roles: [], impersonatedBy: "" });
    localStorage.removeItem("auth"); // { email, accessToken, roles[] }

    socket.send(
      JSON.stringify({
        command: "logout",
        payload: undefined,
      })
    );

    // get rid of cache
    setPrescriptions(null);
    setPublicPrescriptions(null);
    setProcesses(null);
    setExpertProcesses(null);

    Logger.debug(`logout in activity, savechanges is ${saveChanges.current}`);
    if (saveChanges.current) {
      navigate(redirectOnLogout({ location })); // return to this activity after sign-in
    } else {
      Logger.debug(
        `logout in activity, savechanges is ${saveChanges.current} returning to portal`
      );
      navigate(redirectOnLogout({ location: { pathname: "/portal" } }));
    }
  };

  const navigateToPortal = () => {
    navigate("/portal");
  };

  function computeLocationURL(loc: LocationObject) {
    if (loc.pathname && loc.search) {
      return `${loc.pathname}${loc.search}`;
    } else {
      return loc.pathname;
    }
  }

  const navigateToPriorLocation = () => {
    const dst = computeLocationURL(from);
    navigate(dst);
  };

  function beforeNavigatingToDashboard() {
    setSaveAndExitDestination("portal");
    setIsSaveAndExitModalOpen(true);
  }
  function beforeNavigatingToLogout() {
    setSaveAndExitDestination("logout");
    setIsSaveAndExitModalOpen(true);
  }

  function navigateToLogin() {
    navigate("/login", { state: { from: location } });
  }

  function saveAndNavigateToPriorLocation() {
    // TODO: save any input on current page that wasn't saved yet
    navigateToPriorLocation();
  }

  function getDestinationAction(destinationKey: string) {
    if (destinationKey === "portal") {
      return navigateToPortal;
    }
    if (destinationKey === "logout") {
      return logout;
    }
    if (destinationKey == "prior_location") {
      return navigateToPriorLocation;
    }
    return null;
  }

  function afterSaveOrDiscard() {
    setIsSaveAndExitModalOpen(false);
    const destinationAction =
      getDestinationAction(saveAndExitDestination) ?? navigateToPortal;
    destinationAction();
  }

  function renderProfileIconUsername() {
    return (
      <div className="flex items-center gap-2 p-2">
        {/* <Avatar className="h-8 w-8">
          <AvatarImage src="/placeholder.svg?height=32&width=32" />
          <AvatarFallback>JD</AvatarFallback>
        </Avatar> */}
        <div className="h-8 w-8 rounded-full overflow-hidden">
          <img
            src={`https://ui-avatars.com/api/?name=${
              userInfo?.email.split("@")[0]
            }`}
            alt="Avatar"
          />
        </div>
        <div>
          {/* <p className="text-sm font-medium">Umer Pall</p> */}
          {/* text-muted-foreground */}
          <p className="text-xs text-black">
            {userInfo?.email || "N/A"}
          </p>
        </div>
      </div>
    );
  }

  function renderLoginMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToLogin}>
        <LogIn className="mr-2 h-4 w-4" />
        <span>{t("login")}</span>
      </DropdownMenuItem>
    );
  }

  function renderSaveAndExitMenuItem() {
    return (
      <DropdownMenuItem onClick={saveAndNavigateToPriorLocation}>
        <Save className="mr-2 h-4 w-4" />
        <span>{t("link_save_and_exit")}</span>
      </DropdownMenuItem>
    );
  }

  function renderLogoutMenuItem() {
    return (
      <DropdownMenuItem onClick={beforeNavigatingToLogout}>
        <LogOut className="mr-2 h-4 w-4" />
        <span>{t("logout")}</span>
      </DropdownMenuItem>
    );
  }

  function renderDashboardMenuItem() {
    return (
      <DropdownMenuItem onClick={beforeNavigatingToDashboard}>
        <LayoutDashboard className="mr-2 h-4 w-4" />
        <span>{t("link_dashboard")}</span>
      </DropdownMenuItem>
    );   
  }

  function renderAboutThisActivityMenuItem() {
    return (
      <DropdownMenuItem onClick={() => setShowWorkflowInfoDialog(true)}>
        <Info className="mr-2 h-4 w-4" />
        <span>{t("link_about_this_activity")}</span>
      </DropdownMenuItem>
    );
  }

  /*
  function renderShowExamplesItem() {
    return (
      <DropdownMenuItem onClick={() => setShowExamplesDialog(true)}>
        <Eye className="mr-2 h-4 w-4" />
        <span>Examples</span>
      </DropdownMenuItem>
    );
  }
  */

  function renderMenuItems() {
    if (isAuthenticated) {
      return (
        <>
              {renderProfileIconUsername()}
              <DropdownMenuSeparator />
              {renderAboutThisActivityMenuItem()}
              {/* renderShowExamplesItem() */}
              {renderDashboardMenuItem()}
              {renderSaveAndExitMenuItem()}
              <DropdownMenuSeparator />
              {renderLogoutMenuItem()}
        </>
      );
    } else {
      return (
        <>
              {renderLoginMenuItem()}
              <DropdownMenuSeparator />
              {renderAboutThisActivityMenuItem()}
              {/* renderShowExamplesItem() */}
        </>
      );
    }
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="sticky top-0 flex items-center justify-between border-b px-2 md:px-4 py-2 h-10 md:h-14 bg-gray-200">
        <div className="flex items-center">
          {/* <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                {mockActivity.isPrivate ? (
                  <Lock className="h-4 w-4" />
                ) : (
                  <Users className="h-4 w-4" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <h4 className="font-medium">Privacy Settings</h4>
                <div className="space-y-2">
                  <h5 className="text-sm font-medium">Who can see the data?</h5>
                  <p className="text-sm text-muted-foreground">
                    {mockActivity.isPrivate
                      ? "Only you can see this activity's data."
                      : "This activity is shared with specific people."}
                  </p>
                </div>
                <div className="space-y-2">
                  <h5 className="text-sm font-medium">Who can collaborate?</h5>
                  <p className="text-sm text-muted-foreground">
                    {mockActivity.isPrivate
                      ? "No one can collaborate on this activity."
                      : "Collaborators can add comments and suggestions."}
                  </p>
                </div>
              </div>
            </PopoverContent>
          </Popover> */}
        </div>

        <h1 className="text-lg font-medium ml-5 line-clamp-1">
          {selectedPrescription?.name}
        </h1>

        <div className="flex items-center gap-2">
          {isMobile ? (
            <Drawer>
              <DrawerTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 relative"
                >
                  <SquarePen className="h-5 w-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-[80vh]">
                <ChatPanel processId={processId} isMobile={true} />
              </DrawerContent>
            </Drawer>
          ) : (
            <Sheet>
              <SheetTrigger asChild>
                <SquarePen className="h-5 w-5 mr-2.5" />
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle className="text-base">
                    {t("your_takeaways")}
                  </SheetTitle>
                </SheetHeader>
                <ChatPanel processId={processId} isMobile={false} />
              </SheetContent>
            </Sheet>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* <Button variant="ghost" size="icon" className="h-8 w-8"> */}
              <div className="flex flex-col justify-center items-center space-y-[3px] cursor-pointer w-4">
                <span className="w-[5px] h-[5px] bg-gray-700 rounded-full"></span>
                <span className="w-[5px] h-[5px] bg-gray-700 rounded-full"></span>
                <span className="w-[5px] h-[5px] bg-gray-700 rounded-full"></span>
              </div>
              {/* </Button> */}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {renderMenuItems()}
            </DropdownMenuContent>
          </DropdownMenu>

          <Localize />
        </div>
      </div>
      {background && (
        <ActivityRunningPreview
          setActivityLastUpdatedDate={setActivityLastUpdatedDate}
          setPrescriptionVersionNo={setPrescriptionVersionNo}
          setIsActivityCompletedModalOpen={setIsActivityCompletedModalOpen}
          processId={processId}
          setProcessId={setProcessId}
        />
      )}
      {/* <ExamplesDialog
        showExamplesDialog={showExamplesDialog}
        setShowExamplesDialog={setShowExamplesDialog}
        lastViewedExample={lastViewedExample}
        setLastViewedExample={setLastViewedExample}
      /> */}
      <WorkflowInfoDialog
        showWorkflowInfoDialog={showWorkflowInfoDialog}
        setShowWorkflowInfoDialog={setShowWorkflowInfoDialog}
        activityLastUpdatedDate={activityLastUpdatedDate}
        prescriptionVersionNo={prescriptionVersionNo}
        userInfo={userInfo}
      />
      <SaveAndContinueModal
        isDialogOpen={isSaveAndExitModalOpen}
        setIsDialogOpen={afterSaveOrDiscard}
        processId={processId}
        isUserSignedIn={!!auth?.email}
        onSave={() => {
          saveChanges.current = true;
        }}
        onDiscard={() => {
          saveChanges.current = false;
        }}
      />
      {isActivityCompletedModalOpen && (
        <ActivityCompletionModal
          isDialogOpen={isActivityCompletedModalOpen}
          setIsDialogOpen={setIsActivityCompletedModalOpen}
          isUserSignedIn={!!auth?.email}
          processId={processId}
          forwardLocation={forward}
        />
      )}
    </div>
  );
};

export default RunActivityPage;
